// frontend/src/components/RecurringEventModal.js
// 係活動などのイベントを「フルカレンダー UI」から登録・編集・削除する
//
// 機能:
//   - 月表示カレンダー（前月/翌月送り）
//   - 各日のセルにイベントをドットで表示
//   - 日付クリック → その日のイベント一覧 + 追加フォーム
//   - 日付モード: 単日 or 範囲 (event_date 〜 event_end_date)
//   - 対象モード: スキルグループ or スタッフ個別
//   - 範囲モード時の土日祝扱い: 継続 / 休 / 別パターン
//   - 自動シフト割り当て時に、対象のメンバー全員が指定勤務に強制配置される

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { supabase } from '../supabase';
import { writeClientAudit } from '../utils/auditClient';
import { useOrganization } from '../contexts/OrganizationContext';
import { X, ChevronLeft, ChevronRight, Plus, Trash2, Calendar as CalendarIcon, Users, UserCheck } from 'lucide-react';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const RecurringEventModal = ({ isOpen, onClose, initialDate }) => {
  const { currentOrganization } = useOrganization();
  const [currentMonth, setCurrentMonth] = useState(() =>
    dayjs(initialDate || new Date()).startOf('month')
  );
  const [events, setEvents] = useState([]);
  const [skillGroups, setSkillGroups] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // 🔥 編集モード state: 編集中のイベント id (null なら新規追加モード)
  const [editingEventId, setEditingEventId] = useState(null);

  // フォーム state
  const [targetMode, setTargetMode] = useState('group'); // 'group' | 'staff'
  const [dateMode, setDateMode] = useState('single');    // 'single' | 'range'
  const [formState, setFormState] = useState({
    name: '',
    event_date: '',
    event_end_date: '',
    event_dates: [], // 複数日付モード用
    target_skill_group: '',
    target_staff_ids: [],
    target_pattern_id: '',
    weekend_holiday_mode: 'continue',
    weekend_holiday_pattern_id: '',
    description: '',
    block_shift_request: false, // true=シフト希望禁止 / false=アラートのみ
  });
  const [staffFilter, setStaffFilter] = useState('');

  // ============================================
  // データ取得
  // ============================================
  const fetchAll = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const monthStart = currentMonth.format('YYYY-MM-DD');
      const monthEnd = currentMonth.endOf('month').format('YYYY-MM-DD');
      const yearStart = currentMonth.startOf('year').format('YYYY-MM-DD');
      const yearEnd = currentMonth.endOf('year').format('YYYY-MM-DD');
      const [evRes, sgRes, ptRes, stRes, hlRes] = await Promise.all([
        // イベントは「期間が当月と重なるもの」も拾うため、終了日 >= 月初 / 開始日 <= 月末 で広めに取得
        supabase
          .from('recurring_events')
          .select('*')
          .eq('organization_id', currentOrganization.id)
          .lte('event_date', monthEnd)
          .order('event_date'),
        supabase
          .from('skill_groups')
          .select('id, name')
          .eq('organization_id', currentOrganization.id)
          .order('name'),
        supabase
          .from('shift_patterns')
          .select('id, name, short_name, color, configuration')
          .eq('organization_id', currentOrganization.id)
          .eq('is_active', true)
          .order('priority'),
        supabase
          .from('staff')
          .select('id, employee_id, last_name, first_name, job_type_id')
          .eq('organization_id', currentOrganization.id)
          .eq('is_active', true)
          .order('last_name'),
        supabase
          .from('holidays')
          .select('date')
          .or(`organization_id.eq.${currentOrganization.id},is_public.eq.true`)
          .gte('date', yearStart)
          .lte('date', yearEnd),
      ]);
      if (evRes.error) console.warn('recurring_events 取得:', evRes.error.message);
      if (sgRes.error) console.warn('skill_groups 取得:', sgRes.error.message);
      if (ptRes.error) console.warn('shift_patterns 取得:', ptRes.error.message);
      if (stRes.error) console.warn('staff 取得:', stRes.error.message);
      if (hlRes.error) console.warn('holidays 取得:', hlRes.error.message);
      // 月跨ぎ範囲イベントが含まれるよう、終了日が月初以降のものに絞り直す。
      // 複数日付モードの場合は event_dates のいずれかが月内にあれば残す。
      const evs = (evRes.data || []).filter((ev) => {
        if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
          return ev.event_dates.some((d) => d >= monthStart && d <= monthEnd);
        }
        const end = ev.event_end_date || ev.event_date;
        return end >= monthStart && ev.event_date <= monthEnd;
      });
      setEvents(evs);
      setSkillGroups(sgRes.data || []);
      setPatterns(ptRes.data || []);
      setStaffList(stRes.data || []);
      setHolidays((hlRes.data || []).map((h) => h.date));
    } catch (e) {
      console.error('RecurringEventModal 取得エラー:', e);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, currentMonth]);

  useEffect(() => {
    if (isOpen) fetchAll();
  }, [isOpen, fetchAll]);

  // ============================================
  // カレンダーの日付配列（6 週分）
  // ============================================
  const calendarDays = useMemo(() => {
    const firstDay = currentMonth.startOf('month');
    const startDay = firstDay.day();
    const daysInMonth = currentMonth.daysInMonth();
    const cells = [];
    for (let i = startDay; i > 0; i--) {
      cells.push({ date: firstDay.subtract(i, 'day'), isCurrentMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: firstDay.date(d), isCurrentMonth: true });
    }
    while (cells.length < 42) {
      cells.push({ date: cells[cells.length - 1].date.add(1, 'day'), isCurrentMonth: false });
    }
    return cells;
  }, [currentMonth]);

  // 日付別のイベントマップ (範囲イベントは全日に展開 / 複数日付モードは各日個別に)
  const eventsByDate = useMemo(() => {
    const m = {};
    const push = (k, ev) => {
      if (!m[k]) m[k] = [];
      m[k].push(ev);
    };
    events.forEach((ev) => {
      if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
        ev.event_dates.forEach((d) => push(d, ev));
        return;
      }
      const start = dayjs(ev.event_date);
      const end = dayjs(ev.event_end_date || ev.event_date);
      let d = start;
      while (d.isBefore(end) || d.isSame(end, 'day')) {
        push(d.format('YYYY-MM-DD'), ev);
        d = d.add(1, 'day');
      }
    });
    return m;
  }, [events]);

  const filteredStaffList = useMemo(() => {
    if (!staffFilter.trim()) return staffList;
    const q = staffFilter.trim().toLowerCase();
    return staffList.filter((s) => {
      const name = `${s.last_name || ''}${s.first_name || ''}`.toLowerCase();
      return name.includes(q) || String(s.employee_id || '').includes(q);
    });
  }, [staffList, staffFilter]);

  // ============================================
  // 操作
  // ============================================
  const handleDateClick = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    setSelectedDate(dateStr);
    setTargetMode('group');
    setDateMode('single');
    setStaffFilter('');
    setFormState({
      name: '',
      event_date: dateStr,
      event_end_date: dateStr,
      target_skill_group: skillGroups[0]?.name || '',
      target_staff_ids: [],
      target_pattern_id: patterns[0]?.id || '',
      weekend_holiday_mode: 'continue',
      weekend_holiday_pattern_id: '',
      description: '',
      block_shift_request: false,
      event_dates: [],
    });
  };

  const toggleStaff = (staffId) => {
    setFormState((prev) => {
      const cur = new Set(prev.target_staff_ids);
      if (cur.has(staffId)) cur.delete(staffId);
      else cur.add(staffId);
      return { ...prev, target_staff_ids: Array.from(cur) };
    });
  };

  // 🔥 既存イベントを編集モードでロード
  const handleEdit = (ev) => {
    setEditingEventId(ev.id);
    setSelectedDate(ev.event_date); // 編集中の日付に強制移動
    const multi = Array.isArray(ev.event_dates) && ev.event_dates.length > 0;
    setDateMode(multi ? 'multiple'
      : (ev.event_end_date && ev.event_end_date !== ev.event_date ? 'range' : 'single'));
    setTargetMode(ev.target_staff_ids && ev.target_staff_ids.length > 0 ? 'staff' : 'group');
    setFormState({
      name: ev.name || '',
      event_date: ev.event_date,
      event_end_date: ev.event_end_date || ev.event_date,
      event_dates: multi ? ev.event_dates : [],
      target_skill_group: ev.target_skill_group || '',
      target_staff_ids: ev.target_staff_ids || [],
      target_pattern_id: ev.target_pattern_id || '',
      weekend_holiday_mode: ev.weekend_holiday_mode || 'continue',
      weekend_holiday_pattern_id: ev.weekend_holiday_pattern_id || '',
      description: ev.description || '',
      block_shift_request: !!ev.block_shift_request,
    });
  };

  // 🔥 編集キャンセル → 新規追加モードに戻る
  const handleCancelEdit = () => {
    setEditingEventId(null);
    setFormState({
      name: '',
      event_date: selectedDate || '',
      event_end_date: selectedDate || '',
      target_skill_group: skillGroups[0]?.name || '',
      target_staff_ids: [],
      target_pattern_id: patterns[0]?.id || '',
      weekend_holiday_mode: 'continue',
      weekend_holiday_pattern_id: '',
      description: '',
      block_shift_request: false,
      event_dates: [],
    });
  };

  const handleSave = async () => {
    if (!currentOrganization?.id) return;
    if (!editingEventId && !selectedDate) return;
    if (!formState.name.trim()) return alert('イベント名を入力してください');
    if (!formState.target_pattern_id) return alert('強制配置する勤務パターンを選択してください');
    if (targetMode === 'group' && !formState.target_skill_group) {
      return alert('対象スキルグループを選択してください');
    }
    if (targetMode === 'staff' && formState.target_staff_ids.length === 0) {
      return alert('対象スタッフを 1 名以上選択してください');
    }
    if (dateMode === 'range') {
      if (!formState.event_end_date) return alert('終了日を入力してください');
      if (formState.event_end_date < formState.event_date) {
        return alert('終了日は開始日以降にしてください');
      }
      if (formState.weekend_holiday_mode === 'custom' && !formState.weekend_holiday_pattern_id) {
        return alert('土日祝の勤務パターンを選択してください');
      }
    }
    if (dateMode === 'multiple') {
      if (!Array.isArray(formState.event_dates) || formState.event_dates.length === 0) {
        return alert('複数日付モード: 1 日以上の開催日を選択してください');
      }
    }
    setSaving(true);
    try {
      // 複数日付モード時は event_date を最初の日 (ソート後の最古) に、event_end_date は null に
      const sortedMulti = dateMode === 'multiple'
        ? [...new Set(formState.event_dates)].sort()
        : [];
      const payload = {
        organization_id: currentOrganization.id,
        event_date: dateMode === 'multiple'
          ? (sortedMulti[0] || formState.event_date)
          : formState.event_date,
        event_end_date: dateMode === 'range' ? formState.event_end_date : null,
        event_dates: dateMode === 'multiple' ? sortedMulti : null,
        name: formState.name.trim(),
        target_skill_group: targetMode === 'group' ? formState.target_skill_group : null,
        target_staff_ids: targetMode === 'staff' ? formState.target_staff_ids : null,
        target_pattern_id: formState.target_pattern_id,
        weekend_holiday_mode: dateMode === 'range' ? formState.weekend_holiday_mode : 'continue',
        weekend_holiday_pattern_id:
          dateMode === 'range' && formState.weekend_holiday_mode === 'custom'
            ? formState.weekend_holiday_pattern_id
            : null,
        description: formState.description.trim() || null,
        block_shift_request: !!formState.block_shift_request,
      };
      if (editingEventId) {
        const { error } = await supabase
          .from('recurring_events')
          .update(payload)
          .eq('id', editingEventId);
        if (error) throw error;
        await writeClientAudit({
          organizationId: currentOrganization.id,
          actionType: 'update',
          targetTable: 'recurring_events',
          targetId: editingEventId,
          targetSummary: `イベント更新: ${payload.name}`,
          changes: payload,
          success: true,
        });
      } else {
        const { data: inserted, error } = await supabase.from('recurring_events').insert(payload).select().single();
        if (error) throw error;
        await writeClientAudit({
          organizationId: currentOrganization.id,
          actionType: 'create',
          targetTable: 'recurring_events',
          targetId: inserted?.id,
          targetSummary: `イベント追加: ${payload.name}`,
          changes: payload,
          success: true,
        });
      }
      await fetchAll();
      handleCancelEdit();
    } catch (e) {
      console.error('イベント保存エラー:', e);
      // block_shift_request 列が未マイグレーションのときの救済:
      //   PostgREST は "column X does not exist" / 42703 系で返す。
      //   その場合はもう一度 block_shift_request を除外して再試行。
      const msg = e?.message || '';
      const isUnknownCol = /block_shift_request/i.test(msg) && /(column|cache|unknown)/i.test(msg);
      if (isUnknownCol) {
        try {
          const sortedMulti2 = dateMode === 'multiple'
            ? [...new Set(formState.event_dates || [])].sort()
            : [];
          const fallbackPayload = {
            organization_id: currentOrganization.id,
            event_date: dateMode === 'multiple'
              ? (sortedMulti2[0] || formState.event_date)
              : formState.event_date,
            event_end_date: dateMode === 'range' ? formState.event_end_date : null,
            event_dates: dateMode === 'multiple' ? sortedMulti2 : null,
            name: formState.name.trim(),
            target_skill_group: targetMode === 'group' ? formState.target_skill_group : null,
            target_staff_ids: targetMode === 'staff' ? formState.target_staff_ids : null,
            target_pattern_id: formState.target_pattern_id,
            weekend_holiday_mode: dateMode === 'range' ? formState.weekend_holiday_mode : 'continue',
            weekend_holiday_pattern_id:
              dateMode === 'range' && formState.weekend_holiday_mode === 'custom'
                ? formState.weekend_holiday_pattern_id
                : null,
            description: formState.description.trim() || null,
            // block_shift_request は意図的に除外 (migration 未適用の DB にも通るように)
          };
          if (editingEventId) {
            const { error: e2 } = await supabase
              .from('recurring_events').update(fallbackPayload).eq('id', editingEventId);
            if (e2) throw e2;
          } else {
            const { error: e2 } = await supabase.from('recurring_events').insert(fallbackPayload);
            if (e2) throw e2;
          }
          await fetchAll();
          handleCancelEdit();
          alert(
            '保存できました (block_shift_request フラグはマイグレーション未適用のため保存されていません)。' +
            '\n\n「シフト希望の扱い」を本番で有効にするには:\n' +
            '  cd /path/to/ScrubEdge && supabase db push\n' +
            'を実行してください。'
          );
          return;
        } catch (e2) {
          console.error('フォールバック保存も失敗:', e2);
          alert('保存に失敗しました: ' + (e2.message || msg));
          return;
        }
      }
      alert('保存に失敗しました: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('このイベントを削除しますか？')) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('recurring_events')
        .delete()
        .eq('id', eventId);
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'delete',
        targetTable: 'recurring_events',
        targetId: eventId,
        targetSummary: `イベント削除`,
        success: true,
      });
      await fetchAll();
    } catch (e) {
      console.error('イベント削除エラー:', e);
      alert('削除に失敗しました: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // レンダリング
  // ============================================
  if (!isOpen) return null;

  const selectedEvents = selectedDate ? eventsByDate[selectedDate] || [] : [];

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: '16px',
          width: '100%', maxWidth: '1200px', maxHeight: '92vh',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #f0fdf4, #d1fae5)',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#064e3b', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CalendarIcon size={22} />
            係活動・イベント管理
          </h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '6px', borderRadius: '8px', color: '#475569',
          }}>
            <X size={22} />
          </button>
        </div>

        {/* 本体 */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* カレンダー */}
          <div style={{ flex: 1.4, padding: '20px', borderRight: '1px solid #e5e7eb', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <button onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer' }}>
                <ChevronLeft size={18} />
              </button>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                {currentMonth.format('YYYY年 M月')}
              </h3>
              <button onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer' }}>
                <ChevronRight size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '6px' }}>
              {WEEKDAY_LABELS.map((wd, i) => (
                <div key={wd} style={{
                  textAlign: 'center', fontSize: '12px', fontWeight: 700,
                  color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : '#475569',
                  padding: '6px 0',
                }}>{wd}</div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
              {calendarDays.map((cell, i) => {
                const dateStr = cell.date.format('YYYY-MM-DD');
                const dayEvents = eventsByDate[dateStr] || [];
                const isSelected = selectedDate === dateStr;
                const isToday = cell.date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
                const dow = cell.date.day();
                const isHol = holidays.includes(dateStr);
                return (
                  <button key={i} onClick={() => handleDateClick(cell.date)} style={{
                    minHeight: '70px',
                    background: isSelected ? '#dcfce7' : cell.isCurrentMonth ? 'white' : '#f8fafc',
                    border: isSelected ? '2px solid #059669' : '1px solid #e5e7eb',
                    borderRadius: '6px', padding: '4px', cursor: 'pointer',
                    textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '2px',
                    opacity: cell.isCurrentMonth ? 1 : 0.45, position: 'relative',
                  }}>
                    <div style={{
                      fontSize: '12px', fontWeight: isToday ? 800 : 600,
                      color: (dow === 0 || isHol) ? '#dc2626' : dow === 6 ? '#2563eb' : '#0f172a',
                    }}>
                      {cell.date.date()}{isHol && <span style={{ marginLeft: '3px', fontSize: '9px' }}>祝</span>}
                      {isToday && <span style={{ marginLeft: '4px', fontSize: '9px', color: '#059669' }}>今日</span>}
                    </div>
                    {dayEvents.slice(0, 3).map((ev) => (
                      <div key={ev.id} style={{
                        fontSize: '9px', padding: '1px 4px',
                        background: '#10b981', color: 'white', borderRadius: '3px',
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                      }} title={ev.name}>● {ev.name}</div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize: '9px', color: '#059669' }}>+{dayEvents.length - 3}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 詳細パネル */}
          <div style={{ flex: 1, padding: '20px', overflow: 'auto', background: '#f9fafb' }}>
            {!selectedDate ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', marginTop: '40px' }}>
                ← カレンダーから日付を選択
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                  📅 {dayjs(selectedDate).format('YYYY年 M月 D日 (dd)')}
                </h3>

                {/* 既存イベント一覧 */}
                {selectedEvents.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                      登録済イベント ({selectedEvents.length}件)
                    </div>
                    {selectedEvents.map((ev) => {
                      const pat = patterns.find((p) => p.id === ev.target_pattern_id);
                      const whPat = patterns.find((p) => p.id === ev.weekend_holiday_pattern_id);
                      const isRange = !!ev.event_end_date && ev.event_end_date !== ev.event_date;
                      const isStaffMode = Array.isArray(ev.target_staff_ids) && ev.target_staff_ids.length > 0;
                      return (
                        <div key={ev.id} style={{
                          background: 'white', borderRadius: '8px', padding: '10px',
                          marginBottom: '6px', border: '1px solid #d1fae5',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#065f46', fontSize: '14px' }}>{ev.name}</div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                              📅 {ev.event_date}{isRange ? ` 〜 ${ev.event_end_date}` : ''}
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                              {isStaffMode
                                ? <>👤 個別 {ev.target_staff_ids.length}名</>
                                : <>👥 {ev.target_skill_group}</>}
                              {' → '}🏥 {pat?.short_name || pat?.name || '不明'}
                            </div>
                            {isRange && (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                土日祝: {ev.weekend_holiday_mode === 'off' ? '休 (公)'
                                  : ev.weekend_holiday_mode === 'custom'
                                    ? `別勤務 (${whPat?.short_name || whPat?.name || '?'})`
                                    : '同じ勤務'}
                              </div>
                            )}
                            {ev.description && (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{ev.description}</div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <button onClick={() => handleEdit(ev)} disabled={saving}
                              style={{
                                background: editingEventId === ev.id ? '#dbeafe' : '#eff6ff',
                                border: editingEventId === ev.id ? '2px solid #2563eb' : 'none',
                                borderRadius: '6px',
                                padding: '6px', cursor: 'pointer', color: '#1d4ed8',
                              }} title="編集">
                              ✏️
                            </button>
                            <button onClick={() => handleDelete(ev.id)} disabled={saving}
                              style={{
                                background: '#fee2e2', border: 'none', borderRadius: '6px',
                                padding: '6px', cursor: 'pointer', color: '#dc2626',
                              }} title="削除">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 追加/編集フォーム */}
                <div style={{
                  background: 'white', borderRadius: '8px', padding: '12px',
                  border: editingEventId ? '2px solid #2563eb' : '1px solid #e5e7eb',
                }}>
                  <div style={{
                    fontSize: '13px', fontWeight: 700,
                    color: editingEventId ? '#1d4ed8' : '#0f172a',
                    marginBottom: '10px',
                  }}>
                    {editingEventId ? '✏️ イベント編集中' : '➕ 新規イベント追加'}
                  </div>

                  {/* イベント名 (過去イベント名を datalist でサジェスト) */}
                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                    イベント名 <span style={{ color: '#dc2626' }}>*</span>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      placeholder="例: 係活動、院内研修"
                      list="recurring-event-name-suggestions"
                      autoComplete="off"
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: '6px',
                        border: '1px solid #d1d5db', fontSize: '13px', marginTop: '3px',
                      }}
                    />
                    <datalist id="recurring-event-name-suggestions">
                      {Array.from(new Set((events || []).map((ev) => ev.name).filter(Boolean))).map((n) => (
                        <option key={n} value={n} />
                      ))}
                    </datalist>
                  </label>

                  {/* 日付モードタブ */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>開催日</div>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                      <button onClick={() => setDateMode('single')} style={tabStyle(dateMode === 'single')}>
                        単日
                      </button>
                      <button onClick={() => setDateMode('range')} style={tabStyle(dateMode === 'range')}>
                        期間
                      </button>
                      <button onClick={() => setDateMode('multiple')} style={tabStyle(dateMode === 'multiple')}>
                        複数日付
                      </button>
                    </div>
                    {dateMode !== 'multiple' ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="date"
                          value={formState.event_date}
                          onChange={(e) => setFormState({ ...formState, event_date: e.target.value })}
                          style={dateInputStyle}
                        />
                        {dateMode === 'range' && (
                          <>
                            <span style={{ color: '#64748b', fontSize: '12px' }}>〜</span>
                            <input
                              type="date"
                              value={formState.event_end_date}
                              min={formState.event_date}
                              onChange={(e) => setFormState({ ...formState, event_end_date: e.target.value })}
                              style={dateInputStyle}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                        <div style={{ fontSize: '11px', color: '#075985', marginBottom: '6px' }}>
                          複数の開催日を追加 (選択リストから削除できます)
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                          <input
                            type="date"
                            value={formState.event_date}
                            onChange={(e) => setFormState({ ...formState, event_date: e.target.value })}
                            style={dateInputStyle}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (!formState.event_date) return;
                              if ((formState.event_dates || []).includes(formState.event_date)) return;
                              setFormState({
                                ...formState,
                                event_dates: [...(formState.event_dates || []), formState.event_date].sort(),
                              });
                            }}
                            style={{
                              padding: '5px 12px', background: '#0284c7', color: '#fff',
                              border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 700,
                            }}
                          >
                            ＋ 追加
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(formState.event_dates || []).length === 0 ? (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>まだ日付がありません</span>
                          ) : (
                            (formState.event_dates || []).map((d) => (
                              <span
                                key={d}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '2px 8px', background: '#0ea5e9', color: '#fff',
                                  borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                                }}
                              >
                                {d}
                                <button
                                  type="button"
                                  onClick={() => setFormState({
                                    ...formState,
                                    event_dates: (formState.event_dates || []).filter((x) => x !== d),
                                  })}
                                  style={{
                                    background: 'none', border: 'none', color: '#fff',
                                    cursor: 'pointer', fontSize: '12px', padding: 0, lineHeight: 1,
                                  }}
                                  title="削除"
                                >×</button>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 土日祝扱い（範囲モードのみ） */}
                  {dateMode === 'range' && (
                    <div style={{ marginBottom: '8px', padding: '8px', background: '#f0fdf4', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', color: '#065f46', fontWeight: 600, marginBottom: '4px' }}>
                        期間中の土日祝扱い
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="radio" name="whMode" checked={formState.weekend_holiday_mode === 'continue'}
                            onChange={() => setFormState({ ...formState, weekend_holiday_mode: 'continue' })} />
                          同じ勤務を継続
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="radio" name="whMode" checked={formState.weekend_holiday_mode === 'off'}
                            onChange={() => setFormState({ ...formState, weekend_holiday_mode: 'off' })} />
                          土日祝は休 (公) にする
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="radio" name="whMode" checked={formState.weekend_holiday_mode === 'custom'}
                            onChange={() => setFormState({ ...formState, weekend_holiday_mode: 'custom' })} />
                          土日祝は別の勤務に
                        </label>
                        {formState.weekend_holiday_mode === 'custom' && (
                          <select
                            value={formState.weekend_holiday_pattern_id}
                            onChange={(e) => setFormState({ ...formState, weekend_holiday_pattern_id: e.target.value })}
                            style={{ ...dateInputStyle, marginLeft: '20px', maxWidth: '200px' }}
                          >
                            <option value="">選択してください</option>
                            {patterns.map((p) => (
                              <option key={p.id} value={p.id}>{p.short_name} ({p.name})</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 対象タブ */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                      対象 <span style={{ color: '#dc2626' }}>*</span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                      <button onClick={() => setTargetMode('group')} style={tabStyle(targetMode === 'group')}>
                        <Users size={12} style={{ marginRight: '4px' }} /> スキルグループ
                      </button>
                      <button onClick={() => setTargetMode('staff')} style={tabStyle(targetMode === 'staff')}>
                        <UserCheck size={12} style={{ marginRight: '4px' }} /> スタッフ個別
                      </button>
                    </div>

                    {targetMode === 'group' ? (
                      <select
                        value={formState.target_skill_group}
                        onChange={(e) => setFormState({ ...formState, target_skill_group: e.target.value })}
                        style={{ ...dateInputStyle, width: '100%' }}
                      >
                        <option value="">選択してください</option>
                        {skillGroups.map((g) => (
                          <option key={g.id} value={g.name}>{g.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={staffFilter}
                          onChange={(e) => setStaffFilter(e.target.value)}
                          placeholder="名前 / 職員番号で絞り込み"
                          style={{ ...dateInputStyle, width: '100%', marginBottom: '4px' }}
                        />
                        <div style={{
                          maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb',
                          borderRadius: '6px', padding: '4px', background: '#fafafa',
                        }}>
                          {filteredStaffList.length === 0 ? (
                            <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '8px' }}>
                              該当スタッフなし
                            </div>
                          ) : filteredStaffList.map((s) => {
                            const checked = formState.target_staff_ids.includes(s.id);
                            return (
                              <label key={s.id} style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '3px 6px', cursor: 'pointer', fontSize: '12px',
                                background: checked ? '#dcfce7' : 'transparent',
                                borderRadius: '4px',
                              }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleStaff(s.id)}
                                />
                                {s.last_name} {s.first_name}
                                <span style={{ color: '#94a3b8', fontSize: '10px' }}>({s.employee_id})</span>
                              </label>
                            );
                          })}
                        </div>
                        {formState.target_staff_ids.length > 0 && (
                          <div style={{ fontSize: '11px', color: '#059669', marginTop: '4px', fontWeight: 600 }}>
                            ✓ {formState.target_staff_ids.length}名 選択中
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 勤務パターン */}
                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                    強制配置する勤務 <span style={{ color: '#dc2626' }}>*</span>
                    <select
                      value={formState.target_pattern_id}
                      onChange={(e) => setFormState({ ...formState, target_pattern_id: e.target.value })}
                      style={{ ...dateInputStyle, width: '100%', marginTop: '3px' }}
                    >
                      <option value="">選択してください</option>
                      {patterns.map((p) => (
                        <option key={p.id} value={p.id}>{p.short_name} ({p.name})</option>
                      ))}
                    </select>
                  </label>

                  {/* シフト希望の扱い */}
                  <div style={{
                    marginBottom: '10px',
                    padding: '10px 12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px', fontWeight: 600 }}>
                      シフト希望の扱い
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '12px', color: '#334155', marginBottom: 4, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="block_shift_request"
                        checked={!formState.block_shift_request}
                        onChange={() => setFormState({ ...formState, block_shift_request: false })}
                        style={{ marginTop: 3 }}
                      />
                      <div>
                        <strong>アラートのみ (提出は許可)</strong>
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: 2 }}>
                          /form でこの日にシフト希望を入力しようとすると「イベントがあります」と通知するが、入力はできる
                        </div>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="block_shift_request"
                        checked={!!formState.block_shift_request}
                        onChange={() => setFormState({ ...formState, block_shift_request: true })}
                        style={{ marginTop: 3 }}
                      />
                      <div>
                        <strong>希望を提出不可にする (強制配置を優先)</strong>
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: 2 }}>
                          /form でこの日のセルがロックされ、希望入力ができない
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* 補足 */}
                  <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '10px' }}>
                    補足(任意)
                    <textarea
                      value={formState.description}
                      onChange={(e) => setFormState({ ...formState, description: e.target.value })}
                      rows={2}
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: '6px',
                        border: '1px solid #d1d5db', fontSize: '13px', marginTop: '3px', resize: 'vertical',
                      }}
                    />
                  </label>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={handleSave} disabled={saving} style={{
                      padding: '9px', flex: 1,
                      background: saving ? '#9ca3af' : (editingEventId
                        ? 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                        : 'linear-gradient(135deg, #059669, #047857)'),
                      color: 'white', border: 'none', borderRadius: '6px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: 700, fontSize: '13px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}>
                      <Plus size={14} /> {saving
                        ? '保存中...'
                        : (editingEventId ? '更新' : '追加')}
                    </button>
                    {editingEventId && (
                      <button onClick={handleCancelEdit} disabled={saving} style={{
                        padding: '9px 14px',
                        background: '#f3f4f6', color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: '6px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: 600, fontSize: '13px',
                      }}>
                        キャンセル
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
            {loading && (
              <div style={{ marginTop: '12px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                読み込み中...
              </div>
            )}
          </div>
        </div>

        <div style={{
          padding: '12px 24px', borderTop: '1px solid #e5e7eb',
          background: '#f9fafb', fontSize: '11px', color: '#64748b',
        }}>
          💡 イベントは「自動シフト作成」時に対象メンバー全員へ強制配置されます。期間モードでは土日祝の扱いも反映。
        </div>
      </div>
    </div>
  );
};

// 小さな共通スタイル
const tabStyle = (active) => ({
  flex: 1, padding: '6px 10px',
  background: active ? 'linear-gradient(135deg, #10b981, #059669)' : 'white',
  color: active ? 'white' : '#475569',
  border: '1px solid ' + (active ? '#059669' : '#d1d5db'),
  borderRadius: '6px', cursor: 'pointer',
  fontSize: '12px', fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});
const dateInputStyle = {
  padding: '6px 10px', borderRadius: '6px',
  border: '1px solid #d1d5db', fontSize: '12px',
  background: 'white', color: '#0f172a',
};

export default RecurringEventModal;
